import React from 'react';
import { XIcon, CheckCircleIcon, TrendingUpIcon, UsersIcon, DownloadIcon, PlayIcon } from 'lucide-react';
interface SegmentResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  segmentData: {
    name: string;
    accountCount: number;
    confidence: number;
    topIndustries: Array<{
      name: string;
      count: number;
    }>;
    topCompanies: Array<{
      name: string;
      size: string;
      intent: string;
    }>;
  };
}
export function SegmentResultsModal({
  isOpen,
  onClose,
  segmentData
}: SegmentResultsModalProps) {
  if (!isOpen) return null;
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} data-id="element-3146" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3147">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-id="element-3148">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-3149">
            <div className="flex items-center gap-3" data-id="element-3150">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-3151">
                <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-3152" />
              </div>
              <div data-id="element-3153">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-3154">
                  Segment Built Successfully
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3155">
                  AI-powered segment analysis complete
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3156">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3157" />
            </button>
          </div>
          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-3158">
            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-4" data-id="element-3159">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-3160">
                <div className="flex items-center gap-2 mb-2" data-id="element-3161">
                  <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-3162" />
                  <p className="text-sm font-medium text-blue-900" data-id="element-3163">
                    Total Accounts
                  </p>
                </div>
                <p className="text-3xl font-bold text-blue-900" data-id="element-3164">
                  {segmentData.accountCount.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-3165">
                <div className="flex items-center gap-2 mb-2" data-id="element-3166">
                  <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-3167" />
                  <p className="text-sm font-medium text-green-900" data-id="element-3168">
                    Confidence Score
                  </p>
                </div>
                <p className="text-3xl font-bold text-green-900" data-id="element-3169">
                  {segmentData.confidence}%
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-3170">
                <div className="flex items-center gap-2 mb-2" data-id="element-3171">
                  <TrendingUpIcon className="w-5 h-5 text-purple-600" data-id="element-3172" />
                  <p className="text-sm font-medium text-purple-900" data-id="element-3173">
                    Est. Conversion
                  </p>
                </div>
                <p className="text-3xl font-bold text-purple-900" data-id="element-3174">18-22%</p>
              </div>
            </div>
            {/* Top Industries */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3175">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3176">
                Top Industries
              </h3>
              <div className="space-y-3" data-id="element-3177">
                {segmentData.topIndustries.map((industry, index) => <div key={index} data-id="element-3178">
                    <div className="flex items-center justify-between mb-1" data-id="element-3179">
                      <span className="text-sm font-medium text-gray-900" data-id="element-3180">
                        {industry.name}
                      </span>
                      <span className="text-sm text-gray-600" data-id="element-3181">
                        {industry.count} accounts
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-3182">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                    width: `${industry.count / segmentData.accountCount * 100}%`
                  }} data-id="element-3183" />
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Sample Companies */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3184">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3185">
                Sample Companies
              </h3>
              <div className="space-y-2" data-id="element-3186">
                {segmentData.topCompanies.map((company, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-3187">
                    <div className="flex items-center gap-3" data-id="element-3188">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-lg" data-id="element-3189">
                        🏢
                      </div>
                      <div data-id="element-3190">
                        <p className="font-medium text-gray-900" data-id="element-3191">
                          {company.name}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-3192">{company.size}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${company.intent === 'High' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-3193">
                      {company.intent} Intent
                    </span>
                  </div>)}
              </div>
            </div>
            {/* AI Insights */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-3194">
              <h4 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-3195">
                AI Insights
              </h4>
              <ul className="text-sm text-blue-700 space-y-1" data-id="element-3196">
                <li data-id="element-3197">
                  • 67% of accounts show recent funding activity (Series B+)
                </li>
                <li data-id="element-3198">• Average company size: 250-500 employees</li>
                <li data-id="element-3199">
                  • Peak engagement time: Tuesdays and Wednesdays, 10am-2pm EST
                </li>
                <li data-id="element-3200">• Recommended play: High-Intent Account Blitz</li>
              </ul>
            </div>
          </div>
          {/* Actions */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3" data-id="element-3201">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3202">
              Close
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3203">
              <DownloadIcon className="w-4 h-4" data-id="element-3204" />
              Export Segment
            </button>
            <button className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-3205">
              <PlayIcon className="w-4 h-4" data-id="element-3206" />
              Launch Play
            </button>
          </div>
        </div>
      </div>
    </>;
}